import { Box, Divider, TableCell, TableRow, Typography } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** COMPONENT */
import TableRowExport from 'component/Pdf/TableRowExport'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import { numberFormat, toBuddhistYear } from 'utils/app.utils'

/** STYLE */
import { colors } from 'constants/theme'
import * as UseStyled from 'features/report/useStyled'

import { UseSnCode } from 'utils/useSnCode'

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => { }

interface DentistInterface {
  dentistId: number
  dentistFullname: string
}

interface TotalSummary {
  paidTx: number
  cfPaid: number
  dfPaid: number
  paidMed: number
  paidPd: number
  paidMat: number
  paidSt: number
  paidXray: number
  amountPaid: number
  amountCash: number
  amountTransfer: number
  amountDebit: number
  creditCost: number
  amountCredit: number
  amountTreatmentRight: number
  amountAdvance: number
}

interface FcComponent {
  page: number
  pageLimit: number
  rowCount: number
  sortBy: string
  sortType: string
  data: any
  onRequestSort: (sortBy: string, sortType: string) => void
  handleChangeRowsPerPage: (val: any) => void
  handleChangePage: (val: any) => void
  doctors: DentistInterface[]
  filter: any
  filterDateStart: string
  filterDateEnd: string
  componentRef: any
  totalSummary?: TotalSummary
}

export default function RemunerationClinicFeeDentistDccList(props: FcComponent) {
  const { t } = useTranslation()

  const [snType, setSnType] = useState(false)

  const handleSnCode = useCallback(() => {
    const fetchData = async () => {
      const sn = await UseSnCode()
      setSnType(sn)
    }
    fetchData()
  }, [snType])

  useEffect(() => {
    handleSnCode()
  }, [])

  const renderSalaryTable = (data: any) => {
    const items = _.chain(data)
      .map((i: any) => {
        i.dateFormat = toBuddhistYear(moment(i.date), 'DD/MM/YYYY')
        return i
      })
      .groupBy('dateFormat')
      .map((value: any, key: string) => ({ date: key, data: value }))
      .value()
    return items
  }

  // DCC Report Column Headers
  // ลำดับ, เลขที่ใบเสร็จ, ทันตแพทย์, รหัสผู้ป่วย, ชื่อผู้ป่วย, รายการรักษา (Tx), DF, CF, 
  // ยา (Med), สินค้า (Pd), วัสดุอุปกรณ์ (Mat), ST, Xray, ยอดที่จ่ายจริง,
  // เงินสด, เงินโอน, เดบิต, เครดิต%, เครดิต, สิทธิการรักษา, ชำระล่วงหน้า
  const headCells = [
    { id: 'no', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.NO', align: 'center', class: 'text-nowrap' },
    { id: 'billNo', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.BILL_NO', align: 'left', class: 'text-nowrap' },
    { id: 'doctor', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.DOCTOR', align: 'left', class: 'text-nowrap' },
    { id: 'cnNumber', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.PATIENT_CODE', align: 'left', class: 'text-nowrap' },
    { id: 'patientName', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.PATIENT_NAME', align: 'left', class: 'text-nowrap' },
    { id: 'remarkDiscount', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.REMARK_DISCOUNT', align: 'left', class: 'text-nowrap' },
    { id: 'paidTx', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.TX', align: 'right', class: 'text-nowrap' },
    { id: 'dfPaid', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.DF', align: 'right', class: 'text-nowrap' },
    { id: 'cfPaid', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.CF', align: 'right', class: 'text-nowrap' },
    { id: 'paidMed', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.MED', align: 'right', class: 'text-nowrap' },
    { id: 'paidPd', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.PD', align: 'right', class: 'text-nowrap' },
    { id: 'paidMat', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.MAT', align: 'right', class: 'text-nowrap' },
    { id: 'paidSt', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.ST', align: 'right', class: 'text-nowrap' },
    { id: 'paidXray', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.XRAY', align: 'right', class: 'text-nowrap' },
    { id: 'amountPaid', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.AMOUNT_PAID', align: 'right', class: 'text-nowrap' },
    { id: 'amountCash', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.CASH', align: 'right', class: 'text-nowrap' },
    { id: 'amountTransfer', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.TRANSFER', align: 'right', class: 'text-nowrap' },
    { id: 'amountDebit', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.DEBIT', align: 'right', class: 'text-nowrap' },
    { id: 'creditCost', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.CREDIT_PERCENT', align: 'right', class: 'text-nowrap' },
    { id: 'amountCredit', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.CREDIT', align: 'right', class: 'text-nowrap' },
    { id: 'amountTreatmentRight', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.TREATMENT_RIGHT', align: 'right', class: 'text-nowrap' },
    { id: 'amountAdvance', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.ADVANCE', align: 'right', class: 'text-nowrap' }
  ]

  // Section 1: Dentist Fee Headers
  const headDFCells = [
    { id: 'no', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.NO', align: 'center', class: 'text-nowrap' },
    { id: 'doctor', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.DOCTOR', align: 'left', class: 'text-nowrap' },
    { id: 'newPatientCount', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.NEW_PATIENT', align: 'right', class: 'text-nowrap' },
    { id: 'existingPatientCount', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.EXISTING_PATIENT', align: 'right', class: 'text-nowrap' },
    { id: 'totalAmount', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.TOTAL_TREATMENT', align: 'right', class: 'text-nowrap' },
    { id: 'handInsurance', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.HAND_INSURANCE', align: 'right', class: 'text-nowrap' },
    { id: 'amountPaid', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.TOTAL_PAID', align: 'right', class: 'text-nowrap' },
    { id: 'amountCf', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.CF', align: 'right', class: 'text-nowrap' },
    { id: 'amountDf', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.DF', align: 'right', class: 'text-nowrap' },
    { id: 'dfCredit', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.CREDIT_PERCENT', align: 'right', class: 'text-nowrap' },
    { id: 'dfLostTreatment', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.DF_TREATMENT_RIGHT', align: 'right', class: 'text-nowrap' },
    { id: 'dfLostCreditAndTreatment', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.TOTAL_DF', align: 'right', class: 'text-nowrap' },
    { id: 'dfLabFee', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.LAB_FEE', align: 'right', class: 'text-nowrap' },
    { id: 'dfLabFeeDeduct', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.DF_LAB_FEE', align: 'right', class: 'text-nowrap' },
    { id: 'dfNet', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.NET_DF', align: 'right', class: 'text-nowrap' }
  ]

  // Section 2: Income / Net Clinic Headers
  const headTotalCells = [
    { id: 'income', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.INCOME', align: 'right', class: 'text-nowrap' },
    { id: 'netClinic', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.NET_CLINIC', align: 'right', class: 'text-nowrap' },
    { id: 'netDf', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.NET_DF', align: 'right', class: 'text-nowrap' },
    { id: 'dentistFee', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.DENTIST_FEE', align: 'right', class: 'text-nowrap' },
    { id: 'dfLab', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.DF_LAB', align: 'right', class: 'text-nowrap' },
    { id: 'amountCash', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.CASH', align: 'right', class: 'text-nowrap' },
    { id: 'amountTransfer', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.TRANSFER', align: 'right', class: 'text-nowrap' },
    { id: 'amountDebit', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.DEBIT', align: 'right', class: 'text-nowrap' },
    { id: 'creditCost', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.CREDIT_PERCENT', align: 'right', class: 'text-nowrap' },
    { id: 'amountCredit', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.CREDIT', align: 'right', class: 'text-nowrap' },
    { id: 'amountTreatmentRight', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.TREATMENT_RIGHT', align: 'right', class: 'text-nowrap' },
    { id: 'amountAdvance', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.ADVANCE', align: 'right', class: 'text-nowrap' }
  ]

  // LAB section headers
  const headLabCells = [
    { id: 'no', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.NO', align: 'center', class: 'text-nowrap' },
    { id: 'doctor', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.DOCTOR', align: 'left', class: 'text-nowrap' },
    { id: 'cnNumber', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.PATIENT_CODE', align: 'left', class: 'text-nowrap' },
    { id: 'patientName', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.PATIENT_NAME', align: 'left', class: 'text-nowrap' },
    { id: 'labName', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.LAB_NAME', align: 'left', class: 'text-nowrap' },
    { id: 'job', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.JOB', align: 'left', class: 'text-nowrap' },
    { id: 'description', disablePadding: false, labelKey: 'REPORT.TABLE.REMUNERATION_DCC.DESCRIPTION', align: 'left', class: 'text-nowrap' },
    { id: 'labFee', disablePadding: false, labelKey: 'LAB Fee', align: 'right', class: 'text-nowrap' },
    { id: 'dfLabFee', disablePadding: false, labelKey: 'DF LAB Fee', align: 'right', class: 'text-nowrap' }
  ]

  const renderData = (data: any, no: number, exportData: boolean, render: 'data' | 'sum') => {
    if (render === 'data') {

      const {
        billNo,
        branchCnNumber,
        CnNumber,
        patientFullname,
        doctorId,
        paidTx,
        cfPaid,
        dfPaid,
        paidMed,
        paidPd,
        paidMat,
        paidSt,
        paidXray,
        amountPaid,
        amountCash,
        amountTransfer,
        amountDebit,
        amountCredit,
        creditCost,
        amountTreatmentRight,
        amountAdvance,
        remarkDiscount
      } = data
      const num = exportData === true ? no + 1 : props.page * props.pageLimit - props.pageLimit + no + 1
      const objRenderData = {
        key: num.toString(),
        id: no.toString(),
        obj: data,
        columns: [
          { option: 'TEXT', align: 'center', label: num, class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', label: billNo || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', label: _.get(_.find(props.doctors, { dentistId: Number(doctorId) }), 'dentistFullname') || '-', class: 'text-ellipsis' },
          { option: 'TEXT', align: 'left', label:  CnNumber || branchCnNumber  || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', label: patientFullname || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', label: remarkDiscount || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(paidTx || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(dfPaid || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(cfPaid || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(paidMed || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(paidPd || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(paidMat || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(paidSt || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(paidXray || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountPaid || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountCash || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountTransfer || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountDebit || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(creditCost || 0, 2, 2), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountCredit || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountTreatmentRight || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(amountAdvance || 0), class: 'text-nowrap' }
        ]
      }
      return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
    } else {
      // Use totalSummary if available (for all data), otherwise calculate from current page data
      const summary = props.totalSummary || {
        paidTx: _.sumBy(data, 'paidTx') || 0,
        cfPaid: _.sumBy(data, 'cfPaid') || 0,
        dfPaid: _.sumBy(data, 'dfPaid') || 0,
        paidMed: _.sumBy(data, 'paidMed') || 0,
        paidPd: _.sumBy(data, 'paidPd') || 0,
        paidMat: _.sumBy(data, 'paidMat') || 0,
        paidSt: _.sumBy(data, 'paidSt') || 0,
        paidXray: _.sumBy(data, 'paidXray') || 0,
        amountPaid: _.sumBy(data, 'amountPaid') || 0,
        amountCash: _.sumBy(data, 'amountCash') || 0,
        amountTransfer: _.sumBy(data, 'amountTransfer') || 0,
        amountDebit: _.sumBy(data, 'amountDebit') || 0,
        creditCost: _.sumBy(data, 'creditCost') || 0,
        amountCredit: _.sumBy(data, 'amountCredit') || 0,
        amountTreatmentRight: _.sumBy(data, 'amountTreatmentRight') || 0,
        amountAdvance: _.sumBy(data, 'amountAdvance') || 0
      }
      const objRenderData = {
        key: no.toString(),
        id: no.toString(),
        obj: {},
        columns: [
          { option: 'MERGE', align: 'center', colSpan: 5 },
          {
            option: 'TEXT',
            align: 'left',
            label: (
              <Typography className="text-right" sx={{ fontWeight: 500, fontSize: exportData ? 10 : '1rem' }}>
                {t('REPORT.TITLE.SUM')}
              </Typography>
            ),
            class: 'text-nowrap'
          },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.paidTx), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.dfPaid), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.cfPaid), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.paidMed), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.paidPd), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.paidMat), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.paidSt), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.paidXray), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.amountPaid), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.amountCash), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.amountTransfer), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.amountDebit), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.creditCost), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.amountCredit), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.amountTreatmentRight), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(summary.amountAdvance), class: 'text-nowrap' }
        ],
        classes: 'row-summary'

      }

      return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
    }
  }

  const renderLabData = (data: any, no: number, exportData: boolean, render: 'data' | 'sum') => {
    if (render === 'data') {
      const { dentist, branchCnNumber, patientFullname, operativeName, lab, remark, labFee, dfLabFee } = data
      const num = exportData === true ? no + 1 : props.page * props.pageLimit - props.pageLimit + no + 1
      const objRenderData = {
        key: num.toString(),
        id: no.toString(),
        obj: data,
        columns: [
          { option: 'TEXT', align: 'center', label: num, class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', label: dentist || '-', class: 'text-ellipsis' },
          { option: 'TEXT', align: 'left', label: branchCnNumber || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', label: patientFullname || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', label: lab || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', label: operativeName || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'left', label: remark || '-', class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(labFee || 0), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(dfLabFee || 0), class: 'text-nowrap' }
        ]
      }
      return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
    } else {
      const objRenderData = {
        key: no.toString(),
        id: no.toString(),
        obj: data,
        columns: [
          { option: 'MERGE', align: 'center', colSpan: _.size(headLabCells) - 3 },
          {
            option: 'TEXT',
            align: 'left',
            label: (
              <Typography className="text-right" sx={{ fontWeight: 500, fontSize: exportData ? 10 : '1rem' }}>
                {t('REPORT.TITLE.SUM')}
              </Typography>
            ),
            class: 'text-nowrap'
          },
          { option: 'TEXT', align: 'right', label: numberFormat(_.sumBy(data, 'labFee')), class: 'text-nowrap' },
          { option: 'TEXT', align: 'right', label: numberFormat(_.sumBy(data, 'dfLabFee')), class: 'text-nowrap' }
        ],
        classes: 'row-summary'
      }
      return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
    }
  }

  const renderDfData = (data: any, no: number, exportData: boolean) => {
    const { doctorId, newPatientCount, existingPatientCount, totalAmount, handInsurance, amountPaid, amountCf, amountDf, dfCredit, dfLostTreatment, dfLostCreditAndTreatment, labFee, dfLabFee, dfNet } = data
    const num = exportData === true ? no + 1 : props.page * props.pageLimit - props.pageLimit + no + 1
    const objRenderData = {
      key: num.toString(),
      id: no.toString(),
      obj: data,
      columns: [
        { option: 'TEXT', align: 'center', label: num, class: 'text-nowrap' },
        { option: 'TEXT', align: 'left', label: _.get(_.find(props.doctors, { dentistId: Number(doctorId) }), 'dentistFullname') || '-', class: 'text-ellipsis' },
        { option: 'TEXT', align: 'right', label: numberFormat(newPatientCount || 0, 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(existingPatientCount || 0, 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(totalAmount || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(handInsurance || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(amountPaid || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(amountCf || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(amountDf || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(dfCredit || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(dfLostTreatment || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(dfLostCreditAndTreatment || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(labFee || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(dfLabFee || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(dfNet || 0), class: 'text-nowrap' }
      ]
    }
    return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
  }

  const renderIncomeSummary = (summaryIncome: any, exportData: boolean) => {
    if (!summaryIncome) return null

    const objRenderData = {
      key: 'income-summary',
      id: 'income-summary',
      obj: summaryIncome,
      columns: [
        { option: 'TEXT', align: 'right', label: numberFormat(summaryIncome.income || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(summaryIncome.netClinic || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(summaryIncome.netDf || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(summaryIncome.dentistFee || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(summaryIncome.dfLab || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(summaryIncome.amountCash || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(summaryIncome.amountTransfer || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(summaryIncome.amountDebit || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(props?.totalSummary?.creditCost || _.sumBy(props.data?.rawRows, 'creditCost') || 0, 2, 2), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(summaryIncome.amountCredit || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(summaryIncome.amountTreatmentRight || 0), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberFormat(summaryIncome.amountAdvance || 0), class: 'text-nowrap' }
      ]
    }
    return <>{(exportData === true && <TableRowExport {...objRenderData} />) || <TableRowCommon {...objRenderData} />}</>
  }

  return (
    <Box ref={props.componentRef}>
      <Box>
        <Typography className={`mb-1`} sx={{ fontSize: '20px', fontWeight: 600 }}>
          <span className="pr-2">{t('REPORT.TABLE.DOCTOR_FEE')}</span>
        </Typography>
        <UseStyled.ResponsiveReportTable className="mt-2">
          {/* Main Data Table */}
          <TableCustom
            page={props.page}
            pageLimit={props.pageLimit}
            sortBy={props.sortBy}
            sortType={props.sortType}
            headCells={headCells}
            rowCount={props.rowCount}
            rowsData={[
              ...(props.data?.rawRows?.map((row: any, index: number) => renderData(row, index, false, 'data')) || []),
              ...(props.data?.rawRows && props.data.rawRows.length > 0 ? [renderData(props.data.rawRows, 0, false, 'sum')] : [])
            ]}
            onSort={props.onRequestSort}
            setPageLimit={props.handleChangeRowsPerPage}
            setPage={props.handleChangePage}
          />
        </UseStyled.ResponsiveReportTable>
      </Box>

      {/* Section: Patient Count Summary */}
      {props.data?.countTypePatients && (
        <Box mt={4} mb={4}>
          <Typography sx={{ fontSize: '20px', fontWeight: 600, mb: 3 }}>
            {t('REPORT.TITLE.SUMMARY_DENTIST_REMUNERATION')}
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Box
              sx={{
                flex: 1,
                minWidth: '200px',
                border: '1px solid',
                borderColor: colors.lightGray,
                borderRadius: '8px',
                p: 3,
                textAlign: 'center'
              }}
            >
              <Typography sx={{ fontSize: '16px', fontWeight: 500, mb: 1 }}>
                {t('REPORT.SUMMARY.TOTAL_PATIENTS')}
              </Typography>
              <Typography sx={{ fontSize: '24px', fontWeight: 600 }}>
                {numberFormat((props.data.countTypePatients.newPatientCount || 0) + (props.data.countTypePatients.oldPatientCount || 0), 0)} {t('REPORT.SUMMARY.CASES')}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: '200px',
                border: '1px solid',
                borderColor: colors.lightGray,
                borderRadius: '8px',
                p: 3,
                textAlign: 'center'
              }}
            >
              <Typography sx={{ fontSize: '16px', fontWeight: 500, mb: 1 }}>
                {t('REPORT.TABLE.REMUNERATION_DCC.NEW_PATIENT')}
              </Typography>
              <Typography sx={{ fontSize: '24px', fontWeight: 600 }}>
                {numberFormat(props.data.countTypePatients.newPatientCount || 0, 0)}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: '200px',
                border: '1px solid',
                borderColor: colors.lightGray,
                borderRadius: '8px',
                p: 3,
                textAlign: 'center'
              }}
            >
              <Typography sx={{ fontSize: '16px', fontWeight: 500, mb: 1 }}>
                {t('REPORT.TABLE.REMUNERATION_DCC.EXISTING_PATIENT')}
              </Typography>
              <Typography sx={{ fontSize: '24px', fontWeight: 600 }}>
                {numberFormat(props.data.countTypePatients.oldPatientCount || 0, 0)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <UseStyled.ResponsiveReportTable>


        <Divider className="mx-0 mt-4 mb-0" sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }} />

        {/* Section: LAB Summary */}
        <Box mt={4}>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            {t('REPORT.TITLE.LAB_SUMMARY')}
          </Typography>
          <Typography className="mt-2 mb-2">
            <span className="mr-2">{t('MOMENT_AT')}</span>
            {toBuddhistYear(moment(props.filterDateStart), 'DD/MM/YYYY')}
            <span className="mx-1">-</span>
            {toBuddhistYear(moment(props.filterDateEnd), 'DD/MM/YYYY')}
          </Typography>
          <UseStyled.ResponsiveReportTable className="mt-2">
            <TableCustom
              hidePagination
              page={1}
              pageLimit={100}
              sortBy=""
              sortType=""
              headCells={headLabCells}
              rowCount={props.data?.rowLab?.length || 0}
              onSort={noop}
              setPageLimit={noop}
              setPage={noop}
              customScroll
              rowsData={_.map(props.data?.rowLab || [], (d: any) => {
                return (
                  <>
                    <TableRow>
                      <TableCell colSpan={_.size(headLabCells)} sx={{ fontWeight: '500 !important' }}>
                        <span className="mr-2">{t('MOMENT_AT')}</span>
                        {toBuddhistYear(moment(d.date), 'DD/MM/YYYY')}
                      </TableCell>
                    </TableRow>
                    {_.map(d.table, (dataTable: any, indexTable: number) => {
                      return renderLabData(dataTable, indexTable, false, 'data')
                    })}
                    {renderLabData(d.table, 0, false, 'sum')}
                  </>
                )
              })}
            />
          </UseStyled.ResponsiveReportTable>
        </Box>
        <Divider className="mx-0 mt-4 mb-0" sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }} />

        {/* Section: Dentist Fee Summary */}
        <Box mt={4}>
          <Typography sx={{ fontSize: '20px', fontWeight: 500 }}>{t(`REPORT.TABLE.REMUNERATION.DENTIST_FREE`)}</Typography>
          <Typography className="mt-2">
            <span className="mr-2">{t('MOMENT_AT')}</span>
            {toBuddhistYear(moment(props.filterDateStart), 'DD/MM/YYYY')}
            <span className="mx-1">-</span>
            {toBuddhistYear(moment(props.filterDateEnd), 'DD/MM/YYYY')}
          </Typography>
          <TableCustom
            page={1}
            pageLimit={100}
            sortBy=""
            sortType=""
            headCells={headDFCells}
            rowCount={props.data?.rowDf?.length || 0}
            rowsData={props.data?.rowDf?.map((row: any, index: number) => renderDfData(row, index, false)) || []}
            onSort={noop}
            setPageLimit={noop}
            setPage={noop}
            hidePagination
          />
        </Box>

        <Divider className="mx-0 mt-4 mb-0" sx={{ backgroundColor: 'transparent', opacity: 1, borderColor: `${colors.lightGray} !important` }} />

        {/* Section: Income / Net Clinic Summary */}
        {props.data?.summaryIncome && (
          <Box mt={4}>
            <Typography sx={{ fontSize: '20px', fontWeight: 500 }}>Total Income Summary</Typography>
            <TableCustom
              page={1}
              pageLimit={1}
              sortBy=""
              sortType=""
              headCells={headTotalCells}
              rowCount={1}
              rowsData={[renderIncomeSummary(props.data.summaryIncome, false)]}
              onSort={noop}
              setPageLimit={noop}
              setPage={noop}
              hidePagination
            />
          </Box>
        )}
      </UseStyled.ResponsiveReportTable>
    </Box>
  )
}
